import { useState, useEffect, useRef, useCallback } from "react";
import Hls from "hls.js";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Loader2, Volume2, VolumeX, Maximize, Play, Pause } from "lucide-react";
import PricingSection from "@/components/PricingSection";
import { supabase } from "@/integrations/supabase/client";

// ─── CONFIG ───
const STREAM_URL = "https://stream.mux.com/yNq1rwm5W00ooF9d02NLqEoa1nCc4pLZH13UV5nDox7vE.m3u8";
const PLAYBACK_ID = "yNq1rwm5W00ooF9d02NLqEoa1nCc4pLZH13UV5nDox7vE";
const RECONNECT_INTERVAL = 5000;
const VIEWER_POLL_MS = 10000;

const Live = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchStartRef = useRef<number | null>(null);
  const liveEnabledRef = useRef<boolean>(false);

  const [isLive, setIsLive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const keepAliveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const noSleepRef = useRef<{ enable: () => Promise<void> | void; disable: () => void } | null>(null);
  const resumeTimeoutsRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const userPausedRef = useRef(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [volume, setVolume] = useState(100);
  const [viewers, setViewers] = useState<number | null>(null);
  const [liveEnabled, setLiveEnabled] = useState<boolean | null>(null);

  // OTP state
  const FORCE_GATE_KEY = "live_force_gate_ts";
  const VERIFIED_KEY = "live_verified_until";
  const VERIFIED_TTL_MS = 90 * 60 * 1000; // 1h30

  const checkVerified = () => {
    const until = localStorage.getItem(VERIFIED_KEY);
    if (!until) return false;
    if (Date.now() > parseInt(until, 10)) {
      localStorage.removeItem(VERIFIED_KEY);
      return false;
    }
    return true;
  };

  const [showPopup, setShowPopup] = useState(false);
  const [otpStep, setOtpStep] = useState<"email" | "code">("email");
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(() => checkVerified());

  // Keep ref in sync
  useEffect(() => { liveEnabledRef.current = liveEnabled === true; }, [liveEnabled]);

  // ─── HLS PLAYER (stable, never remounts video) ───
  const destroyHls = useCallback(() => {
    if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; }
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
  }, []);

  const initPlayer = useCallback(() => {
    const video = videoRef.current;
    if (!video || !liveEnabledRef.current) return;
    
    // Don't reinit if HLS is already attached and not in error state
    if (hlsRef.current) return;
    
    setIsLoading(true);
    setIsOffline(false);

    if (Hls.isSupported()) {
      const hls = new Hls({
        // Priorité absolue: fluidité + faible latence (qualité visuelle volontairement réduite)
        liveSyncDurationCount: 1,
        liveMaxLatencyDurationCount: 2,
        liveDurationInfinity: true,
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 8,
        maxBufferLength: 8,
        maxMaxBufferLength: 12,
        maxBufferSize: 20 * 1000 * 1000,
        maxBufferHole: 0.1,
        highBufferWatchdogPeriod: 1,
        nudgeOffset: 0.05,
        nudgeMaxRetry: 8,
        startLevel: 0,
        capLevelToPlayerSize: true,
        abrEwmaDefaultEstimate: 1_200_000,
        abrBandWidthUpFactor: 0.5,
        abrBandWidthFactor: 0.7,
        abrMaxWithRealBitrate: true,
        fragLoadingTimeOut: 10000,
        fragLoadingMaxRetry: 8,
        fragLoadingRetryDelay: 500,
        levelLoadingTimeOut: 7000,
        manifestLoadingTimeOut: 7000,
        manifestLoadingMaxRetry: 6,
      });
      hlsRef.current = hls;
      hls.loadSource(STREAM_URL);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        // Forcer une qualité basse pour réduire drastiquement le buffering
        if (hls.levels.length > 1) {
          hls.autoLevelCapping = Math.min(1, hls.levels.length - 1);
          hls.startLevel = 0;
          hls.nextLevel = 0;
          hls.loadLevel = 0;
        }

        setIsLoading(false);
        setIsOffline(false);
        video.play().catch(() => {});
      });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) {
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
          } else if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            setIsLoading(true);
            hls.startLoad();
          } else {
            hls.destroy();
            hlsRef.current = null;
            setIsOffline(true);
            setIsLoading(false);
            // Only reconnect if live is still enabled
            if (liveEnabledRef.current) {
              reconnectTimer.current = setTimeout(initPlayer, RECONNECT_INTERVAL);
            }
          }
        }
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari native HLS
      video.src = STREAM_URL;
      const onMeta = () => { setIsLoading(false); video.play().catch(() => {}); };
      const onError = () => {
        setIsOffline(true);
        setIsLoading(false);
        if (liveEnabledRef.current) {
          reconnectTimer.current = setTimeout(initPlayer, RECONNECT_INTERVAL);
        }
      };
      video.addEventListener("loadedmetadata", onMeta, { once: true });
      video.addEventListener("error", onError, { once: true });
    } else {
      setIsOffline(true);
      setIsLoading(false);
    }
  }, [destroyHls]);

  // ─── REALTIME live_enabled subscription ───
  useEffect(() => {
    // Initial fetch
    const fetchLiveStatus = async () => {
      try {
        const { data } = await supabase
          .from("site_settings")
          .select("value")
          .eq("key", "live_enabled")
          .maybeSingle();
        const value = data?.value as { enabled: boolean } | null;
        setLiveEnabled(value?.enabled ?? false);
      } catch {
        setLiveEnabled(false);
      }
    };
    fetchLiveStatus();

    // Realtime subscription for instant updates
    const channel = supabase
      .channel("live-toggle")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "site_settings", filter: "key=eq.live_enabled" },
        (payload) => {
          const value = payload.new.value as { enabled: boolean };
          setLiveEnabled(value?.enabled ?? false);
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  // ─── Start/stop player based on liveEnabled ───
  useEffect(() => {
    if (liveEnabled === null) return; // still loading
    if (liveEnabled) {
      initPlayer();
    } else {
      destroyHls();
      setIsLive(false);
      setIsPaused(false);
      userPausedRef.current = false;
      setIsLoading(false);
      setIsOffline(false);
    }
    return () => { destroyHls(); };
  }, [liveEnabled, initPlayer, destroyHls]);

  // ─── Video events (stable, no remount) ───
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onPlaying = () => { setIsLive(true); setIsPaused(false); userPausedRef.current = false; setIsOffline(false); setIsLoading(false); };
    const onWaiting = () => setIsLoading(true);
    const onCanPlay = () => setIsLoading(false);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("canplay", onCanPlay);
    return () => {
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("canplay", onCanPlay);
    };
  }, []);

  // ─── WAKE LOCK: prevent screen sleep while live ───
  useEffect(() => {
    const video = videoRef.current;

    const clearKeepAlive = () => {
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
    };

    const disableSleepPrevention = () => {
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
      noSleepRef.current?.disable?.();
      clearKeepAlive();
    };

    const enableNoSleep = async () => {
      try {
        if (!noSleepRef.current) {
          const mod = await import("nosleep.js");
          const NoSleepCtor = mod.default as new () => { enable: () => Promise<void> | void; disable: () => void };
          noSleepRef.current = new NoSleepCtor();
        }
        await noSleepRef.current.enable();
        return true;
      } catch {
        return false;
      }
    };

    const startKeepAlive = () => {
      if (!video || keepAliveIntervalRef.current) return;
      keepAliveIntervalRef.current = setInterval(() => {
        if (liveEnabledRef.current && video.paused) {
          video.play().catch(() => {});
        }
      }, 10000);
    };

    if (!isLive || !liveEnabled) {
      disableSleepPrevention();
      return;
    }

    const requestSleepPrevention = async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLockRef.current = await (navigator as Navigator & { wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinel> } }).wakeLock!.request("screen");
          return;
        }
      } catch {
        // continue with NoSleep fallback
      }

      const noSleepEnabled = await enableNoSleep();
      if (!noSleepEnabled) {
        startKeepAlive();
      }
    };

    const onUserGesture = () => {
      if (!liveEnabledRef.current) return;
      void enableNoSleep();
    };

    const onVisChange = () => {
      if (document.visibilityState === "visible" && isLive && liveEnabled) {
        void requestSleepPrevention();
      }
    };

    void requestSleepPrevention();

    document.addEventListener("visibilitychange", onVisChange);
    document.addEventListener("touchstart", onUserGesture, { passive: true });
    document.addEventListener("click", onUserGesture);

    return () => {
      document.removeEventListener("visibilitychange", onVisChange);
      document.removeEventListener("touchstart", onUserGesture);
      document.removeEventListener("click", onUserGesture);
      disableSleepPrevention();
    };
  }, [isLive, liveEnabled]);

  // ─── Mobile: resume playback on visibility change & fullscreen exit ───
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");

    const clearResumeTimeouts = () => {
      resumeTimeoutsRef.current.forEach(clearTimeout);
      resumeTimeoutsRef.current = [];
    };

    const tryResume = async () => {
      if (!liveEnabledRef.current || userPausedRef.current) return;

      // Native iOS HLS can have no hlsRef attached
      if (!hlsRef.current && !video.currentSrc && !video.src) {
        initPlayer();
      }

      if (!video.paused) return;

      try {
        await video.play();
        setIsPaused(false);
      } catch {
        // iOS autoplay policies can block unmuted resume after fullscreen exit
        if (!video.muted) {
          video.muted = true;
          setIsMuted(true);
        }
        await video.play().catch(() => {});
        if (!video.paused) setIsPaused(false);
      }
    };

    const scheduleResume = () => {
      clearResumeTimeouts();
      [0, 120, 300, 650, 1200, 2000].forEach((delay) => {
        const timeoutId = setTimeout(() => {
          void tryResume();
        }, delay);
        resumeTimeoutsRef.current.push(timeoutId);
      });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        scheduleResume();
      }
    };

    const onFullscreenChange = () => {
      const doc = document as Document & { webkitFullscreenElement?: Element | null };
      const isFS = !!(doc.fullscreenElement || doc.webkitFullscreenElement);
      if (!isFS) {
        scheduleResume();
      }
    };

    const onWebkitEndFullscreen = () => scheduleResume();
    const onPause = () => {
      setIsPaused(true);
      if (userPausedRef.current) return;
      scheduleResume();
    };
    const onPageShow = () => scheduleResume();

    document.addEventListener("visibilitychange", onVisibilityChange);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange as EventListener);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onPageShow);
    video.addEventListener("webkitendfullscreen", onWebkitEndFullscreen as EventListener);
    video.addEventListener("pause", onPause);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange as EventListener);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onPageShow);
      video.removeEventListener("webkitendfullscreen", onWebkitEndFullscreen as EventListener);
      video.removeEventListener("pause", onPause);
      clearResumeTimeouts();
    };
  }, [initPlayer]);

  // ─── FORCE GATE: admin can trigger popup for all non-verified users ───
  useEffect(() => {
    if (isVerified) return;

    // No DB check on mount — force gate only affects users already on the page via Realtime

    // Realtime: listen for new triggers
    const channel = supabase
      .channel("force-gate")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "site_settings", filter: "key=eq.force_gate" },
        (payload) => {
          const value = payload.new.value as { triggered_at: string | null };
          if (value?.triggered_at) {
            localStorage.setItem(FORCE_GATE_KEY, value.triggered_at);
            setShowPopup(true);
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [isVerified]);

  // ─── WATCH TIMER (5 min, persisted across reloads) ───
  useEffect(() => {
    if (isVerified || showPopup || !isLive) return;

    const STORAGE_KEY = "live_watch_elapsed_ms";
    const GATE_MS = 5 * 60 * 1000; // 5 minutes

    const elapsed = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    if (elapsed >= GATE_MS) {
      setShowPopup(true);
      return;
    }

    const remaining = GATE_MS - elapsed;
    const tickStart = Date.now();

    // Save progress every 5 seconds
    const saveInterval = setInterval(() => {
      const now = Date.now();
      const totalElapsed = elapsed + (now - tickStart);
      localStorage.setItem(STORAGE_KEY, String(totalElapsed));
    }, 5000);

    watchTimerRef.current = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, String(GATE_MS));
      setShowPopup(true);
    }, remaining);

    return () => {
      clearInterval(saveInterval);
      if (watchTimerRef.current) clearTimeout(watchTimerRef.current);
      // Save elapsed on cleanup
      const totalElapsed = elapsed + (Date.now() - tickStart);
      localStorage.setItem(STORAGE_KEY, String(totalElapsed));
    };
  }, [isLive, isVerified, showPopup]);

  useEffect(() => {
    return () => { if (watchTimerRef.current) clearTimeout(watchTimerRef.current); };
  }, []);

  // ─── VIEWER COUNT ───
  useEffect(() => {
    if (!liveEnabled) return;
    let active = true;
    const fetchViewers = async () => {
      try {
        const { data } = await supabase.functions.invoke("live-viewers");
        if (active && data?.viewers != null) setViewers(data.viewers);
      } catch { /* silent */ }
    };
    fetchViewers();
    const interval = setInterval(fetchViewers, VIEWER_POLL_MS);
    return () => { active = false; clearInterval(interval); };
  }, [liveEnabled]);

  // ─── CONTROLS ───
  const enableNoSleepFromGesture = () => {
    if (!liveEnabledRef.current) return;
    void (async () => {
      try {
        if (!noSleepRef.current) {
          const mod = await import("nosleep.js");
          const NoSleepCtor = mod.default as new () => { enable: () => Promise<void> | void; disable: () => void };
          noSleepRef.current = new NoSleepCtor();
        }
        await noSleepRef.current.enable();
      } catch {
        // ignore silently
      }
    })();
  };

  const togglePlayPause = () => {
    enableNoSleepFromGesture();
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      userPausedRef.current = false;
      void video.play().then(() => setIsPaused(false)).catch(async () => {
        if (!video.muted) {
          video.muted = true;
          setIsMuted(true);
        }
        await video.play().catch(() => {});
        if (!video.paused) setIsPaused(false);
      });
      return;
    }

    userPausedRef.current = true;
    setIsPaused(true);
    video.pause();
  };

  const toggleMute = () => {
    enableNoSleepFromGesture();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleVolume = (val: number) => {
    enableNoSleepFromGesture();
    const video = videoRef.current;
    if (!video) return;
    video.volume = val / 100;
    setVolume(val);
    if (val > 0 && video.muted) { video.muted = false; setIsMuted(false); }
    if (val === 0) { video.muted = true; setIsMuted(true); }
  };

  const toggleFullscreen = () => {
    enableNoSleepFromGesture();
    const el = containerRef.current as any;
    const video = videoRef.current as any;
    if (!el) return;
    const doc = document as any;
    const isFS = doc.fullscreenElement || doc.webkitFullscreenElement || doc.msFullscreenElement;
    if (isFS) {
      (doc.exitFullscreen || doc.webkitExitFullscreen || doc.msExitFullscreen)?.call(doc);
    } else {
      if (el.requestFullscreen) {
        el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      } else if (el.msRequestFullscreen) {
        el.msRequestFullscreen();
      } else if (video?.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      }
    }
  };

  // ─── OTP HANDLERS ───
  const handleRequestOTP = async () => {
    setOtpError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(otpEmail.trim())) { setOtpError("Email invalide"); return; }
    setOtpLoading(true);
    try {
      const { data } = await supabase.functions.invoke("otp-request", {
        body: { email: otpEmail.trim(), source: "live", playbackId: PLAYBACK_ID },
      });
      if (data?.ok) setOtpStep("code"); else setOtpError("Erreur, réessaye.");
    } catch { setOtpError("Erreur réseau."); }
    setOtpLoading(false);
  };

  const handleVerifyOTP = async () => {
    setOtpError("");
    if (otpCode.length !== 6) { setOtpError("Code à 6 chiffres requis"); return; }
    setOtpLoading(true);
    try {
      const { data } = await supabase.functions.invoke("otp-verify", {
        body: { email: otpEmail.trim(), code: otpCode },
      });
      if (data?.ok) { setIsVerified(true); setShowPopup(false); localStorage.setItem(VERIFIED_KEY, String(Date.now() + VERIFIED_TTL_MS)); localStorage.removeItem(FORCE_GATE_KEY); }
      else if (data?.error === "EXPIRED") setOtpError("Code expiré, renvoie un nouveau code.");
      else if (data?.error === "TOO_MANY_ATTEMPTS") setOtpError("Trop de tentatives, renvoie un nouveau code.");
      else setOtpError("Code incorrect");
    } catch { setOtpError("Erreur réseau."); }
    setOtpLoading(false);
  };

  const handleResendCode = async () => {
    setOtpError(""); setOtpCode(""); setOtpLoading(true);
    try {
      await supabase.functions.invoke("otp-request", { body: { email: otpEmail.trim(), source: "live", playbackId: PLAYBACK_ID } });
      setOtpError("Nouveau code envoyé !");
    } catch { setOtpError("Erreur réseau."); }
    setOtpLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Live - Roi du Streaming</title>
      </Helmet>
      <Header />

      <main className="flex-1 flex flex-col items-center px-4 py-6 md:py-10">
        {/* Hero header */}
        <div className="w-full max-w-[960px] mb-6 text-center">
          <h1 className="text-2xl md:text-4xl font-extrabold text-foreground tracking-wide mb-3">
            📺 REGARDEZ EN DIRECT
          </h1>
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 mb-4">
            <span className="flex items-center gap-1.5 text-xs md:text-sm font-semibold text-primary bg-primary/10 px-3 py-1.5 rounded-full">
              🎬 +100 000 Films
            </span>
            <span className="flex items-center gap-1.5 text-xs md:text-sm font-semibold text-primary bg-primary/10 px-3 py-1.5 rounded-full">
              📡 +15 000 Chaînes TV
            </span>
            <span className="flex items-center gap-1.5 text-xs md:text-sm font-semibold text-primary bg-primary/10 px-3 py-1.5 rounded-full">
              🎭 +25 000 Séries
            </span>
          </div>
        </div>

        {liveEnabled ? (
          <>
            {/* Status + Viewers */}
            <div className="flex items-center gap-3 mb-3">
              {isLive && (
                <div className="flex items-center gap-2">
                  <span className="inline-block h-3 w-3 rounded-full bg-red-500 animate-pulse shadow-[0_0_8px_2px_rgba(239,68,68,0.5)]" />
                  <span className="text-sm font-semibold tracking-wide uppercase text-foreground">
                    EN DIRECT
                  </span>
                </div>
              )}
              {viewers != null && viewers > 0 && (
                <span className="text-xs text-muted-foreground">👁️ {viewers} en direct</span>
              )}
            </div>

            {/* Player container — never unmounted */}
            <div ref={containerRef} className="w-full max-w-[960px] relative rounded-xl overflow-hidden bg-black">
              <div className="relative aspect-video">
                <video
                  ref={videoRef}
                  id="live-player"
                  className={`w-full h-full object-contain transition-all duration-500 ${showPopup && !isVerified ? "blur-xl scale-105" : ""}`}
                  autoPlay
                  muted
                  playsInline
                  style={{ display: "block" }}
                />
                {isLoading && !isOffline && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 pointer-events-none">
                    <Loader2 className="h-10 w-10 text-primary animate-spin" />
                  </div>
                )}
                {isOffline && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 gap-3">
                    <span className="text-lg md:text-xl font-semibold text-white/90">Le live démarre bientôt...</span>
                    <span className="text-sm text-white/50">Reconnexion automatique en cours</span>
                    <Loader2 className="h-6 w-6 text-white/40 animate-spin mt-1" />
                  </div>
                )}
                {showPopup && !isVerified && (
                  <div className="absolute inset-0 backdrop-blur-[30px] bg-black/60 z-10" />
                )}
              </div>
              <div className="flex items-center gap-3 px-3 py-2 bg-black/90">
                <button
                  onClick={togglePlayPause}
                  className="text-white/80 hover:text-white transition-colors p-1"
                  aria-label={isPaused ? "Relancer le live" : "Mettre en pause"}
                >
                  {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                </button>
                <button onClick={toggleMute} className="text-white/80 hover:text-white transition-colors p-1">
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <input
                  type="range" min={0} max={100}
                  value={isMuted ? 0 : volume}
                  onChange={(e) => handleVolume(Number(e.target.value))}
                  className="w-20 h-1 accent-primary cursor-pointer"
                />
                <div className="flex-1" />
                <button onClick={toggleFullscreen} className="text-white/80 hover:text-white transition-colors p-1">
                  <Maximize className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        ) : liveEnabled === null ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
          </div>
        ) : (
          <div className="w-full max-w-[960px] text-center py-8">
            <p className="text-lg text-muted-foreground mb-2">Le live n'est pas en cours actuellement.</p>
            <p className="text-sm text-muted-foreground">Découvrez nos offres ci-dessous pour accéder à tout le contenu !</p>
          </div>
        )}

        {/* ─── VERIFICATION POPUP ─── */}
        {showPopup && !isVerified && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
            <div className="bg-card border border-border rounded-2xl p-6 md:p-8 w-full max-w-md shadow-2xl">
              <h2 className="text-2xl md:text-3xl font-extrabold text-foreground text-center mb-2">🔐 Accès VIP</h2>
              {otpStep === "email" ? (
                <>
                  <p className="text-muted-foreground text-center text-sm mb-2">Inscris-toi pour continuer à profiter du live gratuitement.</p>
                  <p className="text-muted-foreground text-center text-xs mb-1">
                    Tu seras informé(e) de <span className="font-bold text-primary">toutes les prochaines diffusions de matchs</span> en avant-première — bienvenue sur ton nouveau site de streaming préféré 🎬⚽
                  </p>
                  <p className="text-muted-foreground text-center text-xs mb-6">
                    Un <span className="font-bold text-primary">code à 6 chiffres</span> te sera envoyé pour valider ton adresse.
                  </p>
                  <div className="space-y-4">
                    <input type="email" value={otpEmail} onChange={(e) => setOtpEmail(e.target.value)}
                      placeholder="ton@email.com"
                      className="w-full h-12 rounded-lg border border-border bg-input px-4 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      onKeyDown={(e) => e.key === "Enter" && handleRequestOTP()} />
                    <button onClick={handleRequestOTP} disabled={otpLoading}
                      className="w-full h-12 rounded-lg bg-primary text-primary-foreground font-bold text-base hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                      {otpLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "📩 Recevoir mon code"}
                    </button>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <input type="text" inputMode="numeric" maxLength={6} value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    className="w-full h-14 rounded-lg border border-border bg-input px-4 text-center text-2xl font-bold tracking-[0.5em] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    onKeyDown={(e) => e.key === "Enter" && handleVerifyOTP()} />
                  <button onClick={handleVerifyOTP} disabled={otpLoading}
                    className="w-full h-12 rounded-lg bg-primary text-primary-foreground font-bold text-base hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {otpLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Valider"}
                  </button>
                  <button onClick={handleResendCode} disabled={otpLoading} className="w-full text-sm text-primary hover:underline">
                    Renvoyer un code
                  </button>
                </div>
              )}
              {otpError && (
                <p className={`mt-3 text-center text-sm font-bold ${otpError.includes("envoyé") ? "text-green-400" : "text-destructive"}`}>
                  {otpError}
                </p>
              )}
            </div>
          </div>
        )}
      </main>

      <PricingSection />
      <Footer />
    </div>
  );
};

export default Live;
